<div class="flex min-h-0 flex-1 flex-row gap-3 overflow-hidden">
  <!-- Left: groups list card -->
  <section
    class="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white">
    <div class="flex shrink-0 items-center gap-3 border-b border-neutral-100 px-6 py-5">
      <lib-signal-text-field
        class="shrink-0"
        [inputType]="'search'"
        [placeholder]="'Search...'"
        [prefixIcon]="'assets/icons/search.svg'"
        [debounceValueChange]="true"
        (valueChange)="onSearch($event)" />
    </div>

    <div class="min-h-0 flex-1 overflow-auto">
      @if (groups().length > 0) {
        <ul class="divide-y divide-neutral-100">
          @for (group of groups(); track group.id) {
            <li>
              <div
                role="button"
                tabindex="0"
                class="flex w-full cursor-pointer items-center gap-4 px-5 py-4 text-left transition hover:bg-neutral-50"
                [class.bg-primary-50]="selectedGroup()?.id === group.id"
                [class.hover:bg-primary-50]="selectedGroup()?.id === group.id"
                (click)="onRowClicked(group)"
                (keydown.enter)="onRowClicked(group)">
                <span
                  class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
                  [ngClass]="avatarColor(group.id)"
                  aria-hidden="true">
                  {{ groupInitials(group.name) }}
                </span>
                <div class="min-w-0 flex-1">
                  <p class="truncate text-sm font-medium text-neutral-950">
                    {{ group.name }}
                  </p>
                  @if (group.description) {
                    <p class="mt-0.5 truncate text-sm text-neutral-500">
                      {{ group.description }}
                    </p>
                  }
                </div>

                <p class="shrink-0 text-sm text-neutral-500">
                  {{ membersLabel(group.memberCount) }}
                </p>

                <div class="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    class="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-800"
                    aria-label="Edit group"
                    (click)="onEdit($event, group)">
                    <app-svg-icon
                      src="assets/icons/square-edit-outline.svg"
                      [size]="18" />
                  </button>
                  <button
                    type="button"
                    class="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg text-neutral-500 transition hover:bg-neutral-100 hover:text-error-600"
                    aria-label="Delete group"
                    (click)="onDelete($event, group)">
                    <app-svg-icon
                      src="assets/icons/delete-outline.svg"
                      [size]="18" />
                  </button>
                </div>
              </div>
            </li>
          }
        </ul>
      } @else {
        <div class="flex h-full min-h-48 flex-col items-center justify-center p-10 text-center">
          <h3 class="text-base font-semibold text-neutral-950">No groups yet</h3>
          <p class="mt-1 text-sm text-neutral-500">
            @if (listState.loading()) {
              Loading groups…
            } @else {
              Create a group to organize contacts.
            }
          </p>
        </div>
      }
    </div>

    @if (totalCount() > 0) {
      @for (_ of [paginationKey()]; track _) {
        <div class="shrink-0">
          <app-pagination
            [totalItems]="totalCount()"
            [pageSize]="pageSize()"
            [pageSizeOptions]="[10, 25, 50]"
            (pageChange)="onPageChange($event)" />
        </div>
      }
    }
  </section>

  <!-- Right: members panel card -->
  <aside
    class="flex w-[400px] shrink-0 flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white">
    @if (selectedGroup(); as group) {
      <div class="flex shrink-0 items-start justify-between gap-3 border-b border-neutral-100 px-5 py-4">
        <div class="min-w-0">
          <h2 class="truncate text-base font-semibold text-neutral-950">
            {{ group.name }}
          </h2>
          <p class="mt-0.5 text-sm text-neutral-500">
            {{ membersLabel(group.memberCount) }}
          </p>
        </div>
        <lib-signal-button
          appearance="outline"
          iconSrc="assets/icons/account-multiple-plus-outline.svg"
          [fullWidth]="false"
          (clicked)="onAddMembers()">
          Add members
        </lib-signal-button>
      </div>

      <div class="min-h-0 flex-1 overflow-auto">
        @if (members().length > 0) {
          <ul class="divide-y divide-neutral-100">
            @for (member of members(); track member.id) {
              <li class="flex items-center gap-3 px-5 py-3">
                <span
                  class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
                  [ngClass]="avatarColor(member.id)"
                  aria-hidden="true">
                  {{ memberInitials(member) }}
                </span>
                <div class="min-w-0 flex-1">
                  <p class="truncate text-sm font-medium text-neutral-950">
                    {{ memberDisplayName(member) }}
                  </p>
                  <p class="mt-0.5 truncate text-sm text-neutral-500">
                    {{ member.email }}
                  </p>
                </div>
                <button
                  type="button"
                  class="inline-flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-lg text-neutral-500 transition hover:bg-neutral-100 hover:text-error-600"
                  aria-label="Remove member"
                  (click)="onRemoveMember(member)">
                  <app-svg-icon
                    src="assets/icons/delete-outline.svg"
                    [size]="18" />
                </button>
              </li>
            }
          </ul>
        } @else {
          <div class="flex h-full min-h-48 flex-col items-center justify-center px-6 py-10 text-center">
            <p class="text-sm text-neutral-500">
              @if (membersState.loading()) {
                Loading members…
              } @else {
                No members yet. Add contacts to this group.
              }
            </p>
          </div>
        }
      </div>

      <footer class="flex shrink-0 gap-2 border-t border-neutral-100 px-5 py-4">
        <lib-signal-button
          class="flex-1"
          appearance="outline"
          iconSrc="assets/icons/square-edit-outline.svg"
          (clicked)="onEditSelected()">
          Edit group
        </lib-signal-button>
        <lib-signal-button
          class="flex-1"
          appearance="outline"
          iconSrc="assets/icons/delete-outline.svg"
          (clicked)="onDeleteSelected()">
          Delete group
        </lib-signal-button>
      </footer>
    } @else {
      <div class="flex h-full flex-col items-center justify-center px-8 py-10 text-center">
        <div
          class="flex h-12 w-12 items-center justify-center rounded-2xl bg-neutral-100 text-neutral-500">
          <app-svg-icon
            src="assets/icons/account-multiple-outline.svg"
            [size]="24" />
        </div>
        <h3 class="mt-4 text-base font-semibold text-neutral-950">
          Select a group
        </h3>
        <p class="mt-1 max-w-[14rem] text-sm text-neutral-500">
          Select a group to manage its members.
        </p>
      </div>
    }
  </aside>
</div>
